import { useEffect, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import flatMap from 'lodash/flatMap';

interface UseAllPagesOutput<TOutput> {
  isLoading: boolean;
  isError: boolean;
  status: string;
  data: TOutput[];
}

interface OutputResultsResponse<TOutput> {
  results?: TOutput[];
  has_more?: boolean;
  cursor?: string | null;
}

function useAllPages<TOutput, TOutputResponse extends OutputResultsResponse<TOutput>>(
  queryKey: string | any[],
  cursorGetFn: (cursor: string | undefined) => Promise<TOutputResponse>,
  queryOptions?: object
): UseAllPagesOutput<TOutput> {
  const fetchForCursor = async ({ pageParam = undefined }) => {
    return cursorGetFn(pageParam);
  };
  const coercedoptions = queryOptions ?? {};
  const [allData, setAllData] = useState<TOutput[]>([]);
  const [doneFetchingAll, setDoneFetchingAll] = useState<boolean>(false);
  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
    isFetched,
    isLoading,
    isError,
    status,
  } = useInfiniteQuery(typeof queryKey === 'string' ? [queryKey] : queryKey, fetchForCursor, {
    getNextPageParam: lastPage => {
      const castedLastPage = lastPage as TOutputResponse;
      return castedLastPage.has_more ? castedLastPage.cursor : undefined;
    },
    ...coercedoptions,
  });

  useEffect(() => {
    if (hasNextPage && data) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, data]);

  useEffect(() => {
    if (!hasNextPage && data?.pages) {
      setAllData(flatMap(data?.pages, page => page.results ?? []));
      setDoneFetchingAll(true);
    }
  }, [data, hasNextPage]);

  return {
    isLoading: !doneFetchingAll || isFetchingNextPage || isFetching || !isFetched || isLoading,
    data: allData,
    isError,
    status,
  };
}

export default useAllPages;
